import PropTypes from 'prop-types';
import React, { memo, useCallback, useLayoutEffect, useMemo } from 'react';
import _ from 'underscore';
import { FormGroup } from '@bingads-webui-cc-react/layout';

import { dependentOptions } from './constants';
import OptionModel from './option-model';
import TextModel from './text-model';
import { PropTypeModels } from './shapes';
import type { ModelsSectionProps } from './types';

const ModelsSectionUnwrapped = ({
  i18n,
  models,
  values,
  setValue
}: ModelsSectionProps) => {
  const dependentOptionsForModel = useMemo<Record<string, string>>(
    () => _.reduce(
      dependentOptions,
      (dict: Record<string, string>, value: string, key: string) => _.extend(
        dict,
        _.findWhere(models, { Id: key })
          ? { [key]: value }
          : null
      ),
      {} as Record<string, string>
    ),
    [models]
  );
  useLayoutEffect(
    () => {
      _.each(
        dependentOptionsForModel,
        (dependencyKey: string, key: string) => {
          if (values[key] && !values[dependencyKey]) {
            setValue({
              ...values,
              [key]: false,
            });
          }
        }
      );
    },
    [dependentOptionsForModel, setValue, values]
  );
  const modelsWithKeys = useMemo(
    () => models.map(model => ({
      ...model,
      Id: model.Id || _.uniqueId()
    })),
    [models]
  );
  const updateOption = useCallback(
    (key: string, value: boolean) => {
      setValue({
        ...values,
        [key]: value,
      });
    },
    [values, setValue]
  );

  return (
    <div>
      {modelsWithKeys.map((model) => {
        const key = model.Id;
        const dependentOptionKey = dependentOptions[key];

        return (
          <FormGroup key={key} contentColClass="col-sm-12" showLabel={false} isStatic >
            {model.IsReadonly
              ? (
                <TextModel
                  i18n={i18n}
                  localizationKey={model.LocalizationKey}
                  richTextItems={model.RichTextItems}
                />
              )
              : (
                <OptionModel
                  i18n={i18n}
                  id={key}
                  localizationKey={model.LocalizationKey}
                  richTextItems={model.RichTextItems}
                  isSelected={values[key]}
                  setValue={updateOption}
                  disabled={Boolean(dependentOptionKey && !values[dependentOptionKey])}
                />
              )}
          </FormGroup>
        );
      })}
    </div>
  );
};

ModelsSectionUnwrapped.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  models: PropTypeModels.isRequired,
  values: PropTypes.objectOf(PropTypes.bool).isRequired,
  setValue: PropTypes.func.isRequired,
};
ModelsSectionUnwrapped.displayName = 'ModelsSection';

export default memo(ModelsSectionUnwrapped);
